import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as transportActions from '../../actions/transport';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';

const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

interface Props {
    actions: any;
    history: any;
    loading: boolean;
}

const TransportWithoutForm: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage }),
        [intl]
    );

    const [wrId, setWrId] = useState<number | null>(null);
    const [wrData, setWrData] = useState<any>(null);
    const [companyName, setCompanyName] = useState('');
    const [contact, setContact] = useState('');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(props.history.location.search);
        const id = params.get('wr');
        if (id) {
            setWrId(parseInt(id));
            // Fetch WR data from the available items or without-transport endpoint
            props.actions.fetchWithoutTransport({ search: '', page: 1, per_page: 100 }).then((data: any) => {
                const items = data?.data || [];
                const found = items.find((item: any) => String(item.id) === id);
                if (found) setWrData(found);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, []);

    const handleSubmit = async () => {
        if (!wrId) { toast.error(fm('transport.without.toast.notFound', 'Nuk u gjet deponia')); return; }
        setSaving(true);
        try {
            const result = await props.actions.createWithoutTransport({
                warehouse_record_id: wrId,
                company_name: companyName || undefined,
                contact: contact || undefined,
                comment: comment || undefined,
            });
            if (result?.error) {
                toast.error(result.message || fm('transport.form.toast.somethingWrong', 'Diçka shkoi gabim'));
            } else {
                toast.success(fm('transport.without.toast.success', 'Transporti u regjistrua'));
                props.history.push('/dashboard/transport?filter=withoutTransport');
            }
        } catch (err) {
            toast.error(fm('transport.form.toast.somethingWrong', 'Diçka shkoi gabim'));
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="clp-page" style={{ padding: 20 }}>
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></div>
            </div>
        );
    }

    return (
        <div className="clp-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <MikaPageHeader
                title={fm('transport.without.title', 'Pa transport — Konfirmo')}
                onBack={() => props.history.goBack()}
                rightContent={
                    <button
                        className="inst-action-finish"
                        style={{ border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        <FormattedMessage id="transport.without.confirm" defaultMessage="Konfirmo" />
                    </button>
                }
            />

            <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* WR info card */}
                {wrData && (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                            <FormattedMessage id="transport.without.recordHeader" defaultMessage="Deponia" />
                            <span className="t-card-header-badge">{wrData.label}</span>
                        </div>
                        <div className="t-card-body">
                            <div className="t-delivery-info">
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></div>
                                    <div className="t-delivery-value">{wrData.client_name || '—'}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></div>
                                    <div className="t-delivery-value">{wrData.project_name || '—'}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="transport.col.city" defaultMessage="Qyteti" /></div>
                                    <div className="t-delivery-value">{wrData.client_city || '—'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form fields */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        <FormattedMessage id="transport.without.detailsHeader" defaultMessage="Detajet" />
                    </div>
                    <div className="t-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="transport.without.companyLabel" defaultMessage="Kompania e transportit" />
                            </label>
                            <input
                                style={inputStyle}
                                placeholder={fm('transport.without.companyPlaceholder', 'Emri i kompanisë (opsionale)')}
                                value={companyName}
                                onChange={e => setCompanyName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="transport.without.contactLabel" defaultMessage="Kontakti" />
                            </label>
                            <input
                                style={inputStyle}
                                placeholder={fm('transport.without.contactPlaceholder', 'Nr. telefoni (opsionale)')}
                                value={contact}
                                onChange={e => setContact(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="transport.without.commentLabel" defaultMessage="Komenti" />
                            </label>
                            <textarea
                                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' } as React.CSSProperties}
                                placeholder={fm('transport.without.commentPlaceholder', 'Shënim shtesë (opsionale)')}
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(transportActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TransportWithoutForm) as any) as any;
